<template>
 <div class="col-md-12">
  <ConsulteGrid
   :columns="rows"
   :data-source="dataSource"
   :actions="true"
   :show-views-manager="false"
   :show-filter-button="false"
   :show-column-selection-button="false"
   :show-applied-filters="false"
   @on-change-data-row="rowChange"
  >
   <template #header>
    <div class="d-flex align-items-center flex-wrap gap-3" style="margin-left: 10px;">
     <button v-if="permissaoTela.incluir" type="button" class="btn btn-primary rounded-pill" @click="abrirCadastroPopup">
      <i class="uil uil-plus" aria-hidden="true"></i>Cadastrar
     </button>
     <span v-if="nomeUsuarioSelecionado" class="text-muted fw-semibold">
      Usuário: {{ nomeUsuarioSelecionado }}
     </span>
     <div class="form-check mb-0">
      <input
       id="somenteNaoDevolvido"
       v-model="somenteNaoDevolvido"
       class="form-check-input"
       type="checkbox"
       @change="aplicarFiltroSomenteNaoDevolvido"
      />
      <label class="form-check-label" for="somenteNaoDevolvido">Somente não Devolvido</label>
     </div>
     <div class="d-flex align-items-center flex-wrap gap-2">
      <span class="d-inline-flex align-items-center text-muted small">
       <span
        class="rounded-circle d-inline-block me-1"
        style="width: 10px; height: 10px; background-color: #dc3545;"
       ></span>
       Entrega Atrasada
      </span>
      <span class="d-inline-flex align-items-center text-muted small">
       <span
        class="rounded-circle d-inline-block me-1"
        style="width: 10px; height: 10px; background-color: #0d6efd;"
       ></span>
       Devolvido
      </span>
     </div>
    </div>
   </template>
   <template #titulo="context">
    <span :style="{ color: getStatusColor(context.row) }">
     {{ context.row.titulo }}
    </span>
   </template>
   <template #autoria="context">
    <span :style="{ color: getStatusColor(context.row) }">
     {{ context.row.autoria }}
    </span>
   </template>
   <template #dataEmprestimo="context">
    <span :style="{ color: getStatusColor(context.row) }">
     {{ formatDate(context.row.dataEmprestimo) }}
    </span>
   </template>
   <template #dataVencimento="context">
    <span :style="{ color: getStatusColor(context.row) }">
     {{ formatDate(context.row.dataVencimento) }}
    </span>
   </template>
   <template #dataDevolucao="context">
    <span :style="{ color: getStatusColor(context.row) }">
     {{ formatDate(context.row.dataDevolucao) }}
    </span>
   </template>
   <template #botaoDevolucao="context">
    <button type="button"
     v-if="permissaoTela.alterar && !isDevolvido(context.row)"
     class="btn btn-outline-warning"
     @click="devolverExemplar(context.row)"
    >
     Devolver
    </button>
    <button type="button"
     v-else-if="permissaoTela.alterar"
     class="btn btn-outline-danger"
     @click="cancelarDevolucao(context.row)"
    >
     Cancelar
    </button>
   </template>
   <template #botaoRenovar="context">
    <button type="button"
     v-if="permissaoTela.alterar && !isDevolvido(context.row)"
     class="btn btn-outline-warning"
     @click="renovarExemplar(context.row)"
    >
     Renovar
    </button>
   </template>
   <template #botaoEditar="context">
    <button type="button"
     v-if="(permissaoTela.alterar || permissaoTela.excluir) && !isDevolvido(context.row)"
     class="btn btn-outline-warning"
     @click="editarExemplar(context.row)"
    >
     Editar
    </button>
   </template>
  </ConsulteGrid>
  <ModalCadastroEdicao ref="modalCadastroEdicao" @saved="recarregarGrid" @comprovante="abrirComprovante($event, 'emprestimo')" />
  <ModalDevolucao ref="modalDevolucao" @saved="recarregarGrid" @comprovante="abrirComprovante($event, 'devolucao')" />
  <ModalRenovacao ref="modalRenovacao" @saved="recarregarGrid" @comprovante="abrirComprovante($event, 'renovacao')" />
  <ModalComprovante ref="modalComprovante" />
 </div>
</template>

<script lang="ts">
import dialogoService from '@/services/globals/dialogo.service';
import { defineComponent } from "vue";
import ConsulteGrid from "@/components/grid/ConsulteGrid.vue";
import { ConsulteGridColumnsModel, FiltrosModel } from "@/datasources/consulte-grid";
import BibEmprestimoDataSource from "@/datasources/biblioteca/bibemprestimo/index.datasource";
import { BibEmprestimoModel } from "@/models/biblioteca/bibemprestimo/bibemprestimo.model";
import BibEmprestimoService from "@/services/biblioteca/bibemprestimo/bibemprestimo.service";
import { useStore } from "@/store";
import ModalCadastroEdicao from "./ModalCadastroEdicao.vue";
import ModalDevolucao from "./ModalDevolucao.vue";
import ModalRenovacao from "./ModalRenovacao.vue";
import ModalComprovante from "./ModalComprovante.vue";
import { TipoComprovante } from "./comprovante";
import {
 PERMISSAO_TELA_INJECT_KEY,
 PermissaoTela,
 resolverPermissaoTela,
} from "@/composables/usePermissaoTela/usePermissaoTela";
import { eTipoPermissao } from "@/enums/ePermissoes";

export default defineComponent({
 name: "BibEmprestimoGridIndex",
 components: { ConsulteGrid, ModalCadastroEdicao, ModalDevolucao, ModalRenovacao, ModalComprovante },
 provide() {
  // Os modais (cadastro/edição, devolução, renovação) conferem a permissão desta tela.
  return {
   [PERMISSAO_TELA_INJECT_KEY]: this.permissaoTela,
  };
 },
 created() {
  this.criarFiltroSomenteNaoDevolvido("0");
 },
 computed: {
  permissaoTela(): PermissaoTela {
   return resolverPermissaoTela(this.$route.meta?.permissions as eTipoPermissao[] | undefined);
  },
  nomeUsuarioSelecionado(): string {
   const queryValue = this.$route.query.usuarioNome;
   if (Array.isArray(queryValue)) {
    return String(queryValue[0] ?? "");
   }

   return typeof queryValue === "string" ? queryValue : "";
  },
 },
 methods: {
  abrirComprovante(id: number, tipo: TipoComprovante) {
   (this.$refs.modalComprovante as InstanceType<typeof ModalComprovante>).open(id, tipo);
  },
  rowChange(value: string, row: BibEmprestimoModel) {
   console.log(value, row);
  },
  editarExemplar(row: BibEmprestimoModel) {
   (this.$refs.modalCadastroEdicao as any)?.open(this.getContextoTela(), row);
  },
  abrirCadastroPopup() {
   (this.$refs.modalCadastroEdicao as any)?.open(this.getContextoTela());
  },
  devolverExemplar(row: BibEmprestimoModel) {
   (this.$refs.modalDevolucao as any)?.open(row);
  },
  async cancelarDevolucao(row: BibEmprestimoModel) {
   const confirmou = await dialogoService.confirmar({
    mensagem: 'Cancelar a devolução?',
    textoConfirmar: 'Cancelar a devolução',
    textoCancelar: 'Manter',
    perigo: true,
   });
   if (!confirmou) {
    return;
   }

   const payload: BibEmprestimoModel = {
    ...row,
    dataDevolucao: null as any,
    multa: null as any,
    multaPaga: null as any,
    dispensaMulta: null as any,
    bibMotivoDispensaId: null as any,
    devolvido: 0,
    situacao: 0,
   };

   try {
    await BibEmprestimoService.inserirAtualizar(payload);
    this.recarregarGrid();
   } catch (error) {
    console.error("Erro ao cancelar devolucao:", error);
   }
  },
  renovarExemplar(row: BibEmprestimoModel) {
   (this.$refs.modalRenovacao as any)?.open(row);
  },
  getGesEscolaId() {
   const isCentral =
    this.$route.path.includes("/emprestimo/0/") ||
    this.$route.name === "bibemprestimo_grid_central";

   return isCentral ? 0 : useStore().getters.getCodigoEscola;
  },
  getContextoTela() {
   return {
    bibTipoUsuarioId: Number(this.$route.params.bibTipoUsuarioId),
    segUsuarioId: Number(this.$route.params.segUsuarioId),
   };
  },
  recarregarGrid() {
   this.dataSource.load();
  },
  criarFiltroSomenteNaoDevolvido(valor: string) {
   const campoFiltro = "devolvido";
   this.dataSource.filters = this.dataSource.filters.filter((filtro) => filtro.field !== campoFiltro);

   if (this.somenteNaoDevolvido) {
    const filtro = new FiltrosModel(campoFiltro);
    filtro.addFilter(campoFiltro, valor, "pt-Br", "equals", "equals", campoFiltro);
    this.dataSource.filters.push(filtro);
   }
  },
  async aplicarFiltroSomenteNaoDevolvido() {
   this.criarFiltroSomenteNaoDevolvido("0");

   this.dataSource.pageNumber = 0;
   try {
    await this.dataSource.load();
   } catch (error) {
    if (!this.somenteNaoDevolvido) {
     throw error;
    }

    this.criarFiltroSomenteNaoDevolvido("false");
    this.dataSource.pageNumber = 0;
    await this.dataSource.load();
   }
  },
  formatDate(value?: Date | string | null) {
   if (!value) return "";

   const date = value instanceof Date ? value : new Date(value);
   if (Number.isNaN(date.getTime())) return "";

   const day = String(date.getDate()).padStart(2, "0");
   const month = String(date.getMonth() + 1).padStart(2, "0");
   const year = date.getFullYear();
   return `${day}/${month}/${year}`;
  },
  normalizeDate(value?: Date | string | null) {
   if (!value) return null;
   const date = value instanceof Date ? new Date(value) : new Date(value);
   if (Number.isNaN(date.getTime())) return null;
   date.setHours(0, 0, 0, 0);
   return date;
  },
  getStatusColor(row: BibEmprestimoModel) {
   if (Number(row.devolvido) === 1) {
    return "#0d6efd";
   }

   if (Number(row.devolvido) === 0) {
    const dataVencimento = this.normalizeDate(row.dataVencimento);
    if (!dataVencimento) return "";

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    if (dataVencimento < hoje) {
     return "#dc3545";
    }
   }

   return "";
  },
  isDevolvido(row: BibEmprestimoModel) {
   return Number(row.devolvido) === 1;
  },
 },
 data() {
  const bibTipoUsuarioId = Number(this.$route.params.bibTipoUsuarioId);
  const segUsuarioId = Number(this.$route.params.segUsuarioId);
  const gesEscolaId = this.getGesEscolaId();

 return {
   dataSource: new BibEmprestimoDataSource(0, 10, 0, 0, [], bibTipoUsuarioId, segUsuarioId, gesEscolaId),
   somenteNaoDevolvido: true,
   rows: [
    {
     text: "Devolução",
     align: "center",
     cellsalign: "center",
     datafield: "botaoDevolucao",
     editable: false,
     filtertype: "text",
     filterable: false,
    },
    {
     text: "Renovar",
     align: "center",
     cellsalign: "center",
     datafield: "botaoRenovar",
     editable: false,
     filtertype: "text",
     filterable: false,
    },
    {
     text: "Título",
     align: "center",
     cellsalign: "center",
     datafield: "titulo",
     editable: false,
     filtertype: "text",
     filterable: false,
    },
    {
     text: "Autoria",
     align: "center",
     cellsalign: "center",
     datafield: "autoria",
     editable: false,
     filtertype: "text",
     filterable: false,
    },
    {
     text: "Data Empréstimo",
     align: "center",
     cellsalign: "center",
     datafield: "dataEmprestimo",
     editable: false,
     filtertype: "text",
     filterable: false,
    },
    {
     text: "Data Vencimento",
     align: "center",
     cellsalign: "center",
     datafield: "dataVencimento",
     editable: false,
     filtertype: "text",
     filterable: false,
    },
    {
     text: "Data Devolução",
     align: "center",
     cellsalign: "center",
     datafield: "dataDevolucao",
     editable: false,
     filtertype: "text",
     filterable: false,
    },
    {
     text: "Devolvido",
     hidden: true,
     align: "center",
     cellsalign: "center",
     datafield: "devolvido",
     editable: false,
     filtertype: "text",
     filterable: true,
    },
    {
     text: "Editar",
     align: "center",
     cellsalign: "center",
     datafield: "botaoEditar",
     editable: false,
     filtertype: "text",
     filterable: false,
    },
   ] as ConsulteGridColumnsModel[],
  };
 },
});
</script>

